import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNode, useEditor, useEditingCond } from '@splashthat/splash-cms-engine';
import { BRAND_LIBRARY_HEADLINE_TEXT, BRAND_LIBRARY_LIGHT_TEXT_COLOR } from 'common/constants/brandLibrary';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import TextEditable from 'common/components/TouchpointEditor/TextEditable';
import { softSanitizeUrl } from 'common/components/TouchpointEditor/utils';

import { NODE_ID_VIRTUAL_CONTENT, EMBED_TYPES } from '../../../constants';
import { ZoomLinkOutContainer, Link } from './ZoomLinkOut.styles';

export default function ZoomLinkOut({
    styles: { fontColor, fontColorHover, fontSize, fontFamily },
    meta: { href, content, visible },
    selected,
    hovered,
}) {
    const { theme, embeds } = useTouchpointData();
    const node = useNode();
    const editor = useEditor((state) => ({
        editing: state.editing,
        nodes: { [NODE_ID_VIRTUAL_CONTENT]: state.nodes[NODE_ID_VIRTUAL_CONTENT] },
    }));
    const onClickLink = useEditingCond((e) => e.preventDefault());
    const setContent = useCallback(
        (text) =>
            node.actions.setProp((editedNode) => {
                editedNode.meta.content = text; // eslint-disable-line no-param-reassign
                return editedNode;
            }),
        [],
    );
    const isVirtualContentTypeLinkOut =
        editor.nodes?.[NODE_ID_VIRTUAL_CONTENT]?.meta?.virtualContentType === 'link_out';
    const meetingPassword = embeds.data?.embedConfig?.options?.zoom?.meetingPassword;
    const meetingId = embeds.data?.embedConfig?.options?.zoom?.meetingId;
    const defaultButtonHref = meetingId && `https://zoom.us/j/${meetingId}`;
    const shouldRenderElement =
        visible && embeds.data?.embedConfig?.type === EMBED_TYPES.ZOOM && !isVirtualContentTypeLinkOut;

    return shouldRenderElement ? (
        <ZoomLinkOutContainer
            selected={selected}
            hovered={hovered}
            onClick={node.actions.onClick()}
            onMouseLeave={node.actions.onMouseLeave()}
            onMouseMove={node.actions.onMouseMove()}
            $fontColor={theme.colors[fontColor]}
            $fontSize={fontSize}
            $fontFamily={theme.activeFonts[fontFamily]}
        >
            <Link
                $fontColorHover={theme.colors[fontColorHover]}
                href={softSanitizeUrl(href) || defaultButtonHref}
                onClick={onClickLink}
                target="_blank"
            >
                <TextEditable
                    setContent={setContent}
                    disabled={!editor.editing || (editor.editing && !selected && !hovered)}
                >
                    {content}
                </TextEditable>
            </Link>
            {!href && meetingPassword && <span>Password: {meetingPassword} </span>}
        </ZoomLinkOutContainer>
    ) : null;
}

ZoomLinkOut.splash = {
    related: {
        settings: React.lazy(() => import('./ZoomLinkOut.tools')),
    },
    defaultProps: {
        styles: {
            fontColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
            fontColorHover: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
            fontSize: 12,
            fontFamily: BRAND_LIBRARY_HEADLINE_TEXT,
        },
        meta: {
            visible: true,
            href: '',
            content: 'Open this meeting in Zoom',
        },
    },
};

ZoomLinkOut.propTypes = {
    selected: PropTypes.bool.isRequired,
    hovered: PropTypes.bool.isRequired,
    styles: PropTypes.shape({
        fontColor: PropTypes.number,
        fontColorHover: PropTypes.number,
        fontSize: PropTypes.number,
        fontFamily: PropTypes.number,
    }).isRequired,
    meta: PropTypes.shape({
        content: PropTypes.string,
        href: PropTypes.string,
        visible: PropTypes.bool,
    }).isRequired,
};
